import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, fetchWithRetry } from '../config';
import Cookies from 'universal-cookie';
import '../styles/profile.css';
import EditProfileModal from '../components/EditProfileModal';
import ProfilePicture from '../components/ProfilePicture';

const Profile = () => {
    const [profile, setProfile] = useState(null);
    const [recipes, setRecipes] = useState([]);
    const [isFollowing, setIsFollowing] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showFollowing, setShowFollowing] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const { userId } = useParams();
    const currentUserId = localStorage.getItem('userID');
    const cookies = new Cookies();
    const navigate = useNavigate();

    // Check if user is authenticated
    const token = cookies.get('access_token');
    if (!token || !currentUserId) {
        return <Navigate to="/auth/login" replace />;
    }

    useEffect(() => {
        fetchProfile();
    }, [userId, currentUserId]);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const data = await fetchWithRetry(`${API_BASE_URL}/users/profile/${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            
            if (data.user) {
                setProfile(data.user);
                setRecipes(data.recipes || []);
                setIsFollowing(data.user.followers.some(
                    follower => follower._id === currentUserId
                ));
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            setError(error.message || 'Error loading profile');
            if (error.response?.status === 401) {
                navigate('/auth', { replace: true });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFollow = async () => {
        try {
            const token = cookies.get('access_token');
            if (!token || !currentUserId) {
                console.log('No token or userID found:', { token: !!token, currentUserId });
                navigate('/auth/login', { replace: true });
                return;
            }

            // Log the current token for debugging
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                console.log('Token payload:', {
                    exp: new Date(payload.exp * 1000),
                    id: payload.id
                });
            } catch (e) {
                console.error('Error parsing token:', e);
            }

            const endpoint = isFollowing ? 'unfollow' : 'follow';
            console.log('Starting follow request:', {
                endpoint,
                userId,
                currentUserId,
                hasToken: !!token
            });
            
            const response = await fetchWithRetry(
                `${API_BASE_URL}/users/${endpoint}/${userId}`,
                {
                    method: 'POST',
                    body: JSON.stringify({ userId: currentUserId })
                }
            );
            
            console.log('Follow response:', response);
            setIsFollowing(!isFollowing);
            await fetchProfile();
        } catch (error) {
            console.error('Error following/unfollowing:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            
            if (error.message.includes('Invalid or expired token') || 
                error.message.includes('Token has expired') || 
                error.response?.status === 401) {
                cookies.remove('access_token');
                localStorage.removeItem('userID');
                navigate('/auth/login', { replace: true });
            } else {
                alert(`Error: ${error.message || 'Could not update follow status'}`);
            }
        }
    };

    const handleProfileUpdate = (updatedUser) => {
        setProfile(updatedUser);
    };

    if (loading) return <div className="loading">Loading profile...</div>;
    if (error) return <div className="error">{error}</div>;
    if (!profile) return <div className="error">Profile not found</div>;

    return (
        <div className="profile-container">
            <div className="profile-header">
                <ProfilePicture 
                    src={profile.profilePicture}
                    alt={profile.username}
                    className="profile-picture"
                />
                <div className="profile-info">
                    <h2>{profile.username}</h2>
                    <p className="bio">{profile.bio || 'No bio yet'}</p>
                    <div className="profile-stats">
                        <span>{recipes.length} Recipes</span>
                        <span>{profile.followers?.length || 0} Followers</span>
                        <span 
                            className="following-count"
                            onClick={() => setShowFollowing(!showFollowing)}
                            style={{ cursor: 'pointer' }}
                        >
                            {profile.following?.length || 0} Following
                        </span>
                    </div>
                    {currentUserId === userId && (
                        <button 
                            className="edit-profile-button"
                            onClick={() => setShowEditModal(true)}
                        >
                            Edit Profile
                        </button>
                    )}
                    {currentUserId !== userId && (
                        <button 
                            className={`follow-button ${isFollowing ? 'following' : ''}`}
                            onClick={handleFollow}
                        >
                            {isFollowing ? 'Unfollow' : 'Follow'}
                        </button>
                    )}
                </div>
            </div>

            {showEditModal && (
                <EditProfileModal
                    user={profile}
                    onClose={() => setShowEditModal(false)}
                    onUpdate={handleProfileUpdate}
                    token={cookies.get('access_token')}
                />
            )}

            {showFollowing && profile.following && profile.following.length > 0 && (
                <div className="following-list">
                    <h3>Following</h3>
                    <div className="following-grid">
                        {profile.following.map(user => (
                            <Link 
                                to={`/profile/${user._id}`}
                                key={user._id}
                                className="following-card"
                                onClick={() => setShowFollowing(false)}
                            >
                                <ProfilePicture
                                    src={user.profilePicture}
                                    alt={user.username}
                                    className="following-avatar"
                                />
                                <span>{user.username}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="profile-recipes">
                <h3>Recipes</h3>
                {recipes.length === 0 ? (
                    <p className="no-recipes">No recipes yet</p>
                ) : (
                    <div className="recipes-grid">
                        {recipes.map(recipe => (
                            <Link 
                                to={`/recipe/${recipe._id}`} 
                                key={recipe._id}
                                className="recipe-card"
                            >
                                <img 
                                    src={recipe.imageUrl} 
                                    alt={recipe.name} 
                                    className="recipe-image"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/default-recipe.png';
                                    }}
                                />
                                <div className="recipe-info">
                                    <h4>{recipe.name}</h4>
                                    <p>Cooking Time: {recipe.cookingTime} minutes</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile; 